import { Target, Users, Wallet, Trophy, CheckCircle2, TrendingUp, Sparkles, ArrowUpRight, Clock, CalendarRange, Compass } from "lucide-react";

const PRIORITY_STYLE = {
  Immediate: "bg-red-50 text-red-700 border-red-200",
  "Medium Term": "bg-amber-50 text-amber-800 border-amber-200",
  Strategic: "bg-blue-50 text-blue-700 border-blue-200",
};

const PRIORITY_ICON = {
  Immediate: Clock,
  "Medium Term": CalendarRange,
  Strategic: Compass,
};

export default function BriefBody({ brief }) {
  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Company Snapshot */}
      <section
        data-testid="company-snapshot-card"
        className="col-span-12 lg:col-span-4 rounded-2xl border border-slate-200 p-6 sm:p-7 bg-white card-lift"
      >
        <div className="flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-slate-500 mb-4">
          <span className="h-1.5 w-1.5 rounded-full bg-electric" />
          Company Snapshot
        </div>
        <h2 className="font-display text-2xl font-bold text-navy tracking-tight">
          {brief.target_company}
        </h2>
        <span className="mt-2 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-50 text-electric border border-blue-100">
          {brief.snapshot.market_position}
        </span>

        <dl className="mt-6 space-y-5">
          <SnapField icon={Target} label="What they do" value={brief.snapshot.what_they_do} />
          <SnapField icon={Users} label="Target customer" value={brief.snapshot.target_customer} />
          <SnapField icon={Wallet} label="Business model" value={brief.snapshot.business_model} />
        </dl>
      </section>

      {/* Competitor Table */}
      <section
        data-testid="competitors-table"
        className="col-span-12 lg:col-span-8 rounded-2xl border border-slate-200 p-6 sm:p-7 bg-white card-lift"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-slate-500">
            <Trophy className="h-3.5 w-3.5" />
            Top 4 Competitors
          </div>
          <span className="text-xs text-slate-400">Ranked by relevance</span>
        </div>

        <div className="overflow-x-auto thin-scroll -mx-2">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-[11px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="px-3 py-3 font-semibold">Competitor</th>
                <th className="px-3 py-3 font-semibold">Positioning</th>
                <th className="px-3 py-3 font-semibold">Key Strength</th>
                <th className="px-3 py-3 font-semibold">Key Weakness</th>
              </tr>
            </thead>
            <tbody>
              {brief.competitors.map((c, i) => (
                <tr
                  key={c.name + i}
                  data-testid={`competitor-row-${i}`}
                  className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors"
                >
                  <td className="px-3 py-4 align-top">
                    <div className="font-semibold text-navy">{c.name}</div>
                    <div className="mt-1 text-[11px] font-mono text-slate-400">
                      P{c.price_score} · F{c.feature_score}
                    </div>
                  </td>
                  <td className="px-3 py-4 align-top text-slate-700">{c.positioning}</td>
                  <td className="px-3 py-4 align-top">
                    <div className="flex gap-2 items-start">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{c.strength}</span>
                    </div>
                  </td>
                  <td className="px-3 py-4 align-top">
                    <div className="flex gap-2 items-start">
                      <TrendingUp className="h-4 w-4 text-rose-500 rotate-180 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{c.weakness}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Positioning Map */}
      <section
        data-testid="positioning-map-chart"
        className="col-span-12 lg:col-span-6 rounded-2xl border border-slate-200 p-6 sm:p-7 bg-white card-lift"
      >
        <div className="flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-slate-500 mb-4">
          <span className="h-1.5 w-1.5 rounded-full bg-electric" />
          Positioning Map · Price × Feature Depth
        </div>
        <PositioningMap competitors={brief.competitors} />
      </section>

      {/* Feature Gap Analysis */}
      <section
        data-testid="feature-gap-card"
        className="col-span-12 lg:col-span-6 rounded-2xl border border-slate-200 p-6 sm:p-7 bg-white card-lift"
      >
        <div className="flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-slate-500 mb-4">
          <span className="h-1.5 w-1.5 rounded-full bg-electric" />
          Feature Gap Analysis
        </div>
        <div className="space-y-5">
          <GapBlock
            tone="slate"
            title="Must-Have Parity"
            items={brief.feature_gaps.must_have_parity}
          />
          <GapBlock
            tone="rose"
            title="Competitor Lead"
            items={brief.feature_gaps.competitor_lead}
          />
          <GapBlock
            tone="blue"
            title="Differentiation Opportunity"
            items={brief.feature_gaps.differentiation_opportunity}
          />
        </div>
      </section>

      {/* Opportunities */}
      <section className="col-span-12 lg:col-span-7">
        <div className="flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-slate-500 mb-4">
          <Sparkles className="h-3.5 w-3.5" />
          Product Opportunities
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {brief.opportunities.map((op, i) => (
            <div
              key={i}
              data-testid={`opportunity-card-${i}`}
              className="rounded-2xl border border-slate-200 p-5 bg-white card-lift relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 h-1 w-full bg-electric" />
              <div className="text-[11px] font-mono tracking-widest uppercase text-slate-400 mb-2">
                Opportunity {i + 1}
              </div>
              <h3 className="font-display text-lg font-bold text-navy leading-snug">
                {op.title}
              </h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{op.rationale}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PM Next Steps */}
      <section
        data-testid="pm-next-steps-list"
        className="col-span-12 lg:col-span-5 rounded-2xl border border-slate-200 p-6 sm:p-7 bg-white card-lift"
      >
        <div className="flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-slate-500 mb-4">
          <ArrowUpRight className="h-3.5 w-3.5" />
          PM Next Steps
        </div>
        <ul className="space-y-3">
          {brief.next_steps.map((s, i) => {
            const Icon = PRIORITY_ICON[s.priority] || Clock;
            return (
              <li
                key={i}
                data-testid={`next-step-${i}`}
                className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0"
              >
                <div className={`flex-shrink-0 mt-0.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${PRIORITY_STYLE[s.priority] || PRIORITY_STYLE.Strategic}`}>
                  <Icon className="h-3 w-3" />
                  {s.priority}
                </div>
                <span className="text-sm text-slate-700 leading-relaxed">{s.action}</span>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

function SnapField({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] font-mono tracking-widest uppercase text-slate-500 mb-1">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <p className="text-sm text-slate-800 leading-relaxed">{value}</p>
    </div>
  );
}

function GapBlock({ title, items, tone }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    blue: "bg-blue-50 text-electric border-blue-200",
  };
  return (
    <div>
      <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${tones[tone]}`}>
        {title}
      </div>
      <ul className="mt-2 space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-slate-700 leading-relaxed flex gap-2">
            <span className="text-slate-300 mt-1">•</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PositioningMap({ competitors }) {
  // 400x300 SVG. x = feature_score (0-100), y = price_score (0-100 -> inverted).
  const W = 480;
  const H = 320;
  const P = 40;

  const points = competitors.map((c) => ({
    name: c.name,
    x: P + (c.feature_score / 100) * (W - 2 * P),
    y: H - P - (c.price_score / 100) * (H - 2 * P),
  }));

  return (
    <div className="w-full">
      <div className="relative w-full aspect-[3/2]">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
          {/* Quadrant background */}
          <rect x={P} y={P} width={W - 2 * P} height={H - 2 * P} fill="#F8FAFC" />
          {/* Cross lines */}
          <line x1={W / 2} y1={P} x2={W / 2} y2={H - P} stroke="#E2E8F0" strokeDasharray="4 4" />
          <line x1={P} y1={H / 2} x2={W - P} y2={H / 2} stroke="#E2E8F0" strokeDasharray="4 4" />
          {/* Axes */}
          <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#0B2545" strokeWidth="1.5" />
          <line x1={P} y1={P} x2={P} y2={H - P} stroke="#0B2545" strokeWidth="1.5" />

          {/* Quadrant labels */}
          <text x={P + 8} y={P + 16} fontSize="10" fill="#64748B" fontFamily="JetBrains Mono">PREMIUM · SHALLOW</text>
          <text x={W - P - 8} y={P + 16} fontSize="10" fill="#64748B" fontFamily="JetBrains Mono" textAnchor="end">PREMIUM · DEEP</text>
          <text x={P + 8} y={H - P - 8} fontSize="10" fill="#64748B" fontFamily="JetBrains Mono">ECONOMY · SHALLOW</text>
          <text x={W - P - 8} y={H - P - 8} fontSize="10" fill="#64748B" fontFamily="JetBrains Mono" textAnchor="end">MASS · DEEP</text>

          {/* Axis titles */}
          <text x={W / 2} y={H - 8} fontSize="11" fill="#0B2545" fontWeight="600" textAnchor="middle">Feature Depth →</text>
          <text x={12} y={H / 2} fontSize="11" fill="#0B2545" fontWeight="600" textAnchor="middle" transform={`rotate(-90, 12, ${H / 2})`}>Price / Take Rate →</text>

          {/* Points */}
          {points.map((p, i) => (
            <g key={p.name + i}>
              <circle cx={p.x} cy={p.y} r="9" fill="#1E5AFF" opacity="0.15" />
              <circle cx={p.x} cy={p.y} r="5" fill="#1E5AFF" stroke="#ffffff" strokeWidth="2" />
              <text
                x={p.x + 10}
                y={p.y + 4}
                fontSize="11"
                fontWeight="600"
                fill="#0B2545"
                fontFamily="Inter"
              >
                {p.name}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}
